import { Application, Container } from 'pixi.js';
import { createPinia } from 'pinia';
import { Game } from './game/Game';
import { UI } from './components/UI';
import { LoadingScreen } from './components/LoadingScreen';
import { useGameStore } from './stores/gameStore';
import { getUrlParams, validateUrlParams } from './utils/urlParams';
import { AudioManager } from './utils/AudioManager';
import { GAME_CONFIG } from './config/GameConfig';
import type { BetDto } from './types/api';

class SlotGame {
  private app!: Application;
  private game!: Game;
  private ui!: UI;
  private loadingScreen!: LoadingScreen;
  private audioManager!: AudioManager;
  private gameStore!: ReturnType<typeof useGameStore>;
  private pinia!: ReturnType<typeof createPinia>;
  
  private betIndex: number = 0;
  private autoSpinRounds: number = 0;
  private autoSpinTotal: number = 0;
  private isAutoSpinning: boolean = false;
  
  async init(): Promise<void> {
    // Set hash for consistency with other games
    const hash = window.location.hash;
    if (!hash || hash === '' || hash === '#') {
      window.location.hash = '/';
    }
    
    this.audioManager = AudioManager.getInstance();
    
    // Initialize Pinia
    this.pinia = createPinia();
    this.gameStore = useGameStore(this.pinia);
    
    // Fixed aspect ratio for mobile portrait
    const gameWidth = 768;
    const gameHeight = 1024;
    
    this.app = new Application();
    
    await this.app.init({
      width: gameWidth,
      height: gameHeight,
      background: 0x1a1a2e,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    
    document.getElementById('app')?.appendChild(this.app.canvas);
    
    // Center the stage
    this.app.stage.x = this.app.screen.width / 2;
    this.app.stage.y = this.app.screen.height / 2;
    
    // Enable proper z-ordering
    this.app.stage.sortableChildren = true;
    
    // Show loading screen
    this.loadingScreen = new LoadingScreen();
    this.app.stage.addChild(this.loadingScreen);
    
    // Parse URL parameters
    const urlParams = getUrlParams();
    const validation = validateUrlParams(urlParams);
    
    if (!validation.valid) {
      this.showError(`Missing parameters: ${validation.error}`);
      return;
    }
    
    // Initialize game with backend
    try {
      await this.initializeGame(urlParams);
    } catch (error) {
      console.error('Initialization error:', error);
      this.showError('Failed to connect to server. Please refresh the page.');
      return;
    }
    
    // Setup game loop
    this.app.ticker.add((ticker) => {
      this.game.update(ticker.deltaTime);
      this.ui.update(ticker.deltaTime);
    });
    
    this.centerCanvas();
    window.addEventListener('resize', () => this.centerCanvas());
  }
  
  private async initializeGame(params: ReturnType<typeof getUrlParams>): Promise<void> {
    this.loadingScreen.setLoadingAssets();
    
    try {
      // Initialize game store with backend
      await this.gameStore.initGame(params);
      
      this.loadingScreen.updateProgress(30, 'Loading game...');
      await this.delay(200);
      
      // Create UI
      this.ui = new UI(this.app.stage, this.pinia);
      
      this.loadingScreen.updateProgress(50, 'Creating reels...');
      await this.delay(200);
      
      // Create game
      this.game = new Game(this.app, this.pinia);
      await this.game.init();
      
      this.loadingScreen.updateProgress(70, 'Setting up controls...');
      await this.delay(200);
      
      // Setup callbacks
      this.setupUICallbacks();
      this.updateUI();
      
      this.loadingScreen.setInitializing();
      await this.delay(200);
      
      // Complete
      this.loadingScreen.complete(() => {
        console.log('🎰 Fruit Paradise Slot Machine initialized!');
        console.log(`Balance: ${this.gameStore.balance} ${this.gameStore.currency}`);
      });
      
    } catch (error) {
      throw error;
    }
  }
  
  private showError(message: string): void {
    this.loadingScreen.updateProgress(0, message);
    // Don't call complete() - keep loading screen visible with error
  }
  
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  private centerCanvas(): void {
    const canvas = this.app.canvas;
    
    if (canvas instanceof HTMLCanvasElement) {
      const gameWidth = 768;
      const gameHeight = 1024;
      const gameAspect = gameWidth / gameHeight;
      
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const windowAspect = windowWidth / windowHeight;
      
      let canvasWidth: number;
      let canvasHeight: number;
      
      if (windowAspect > gameAspect) {
        canvasHeight = windowHeight;
        canvasWidth = windowHeight * gameAspect;
      } else {
        canvasWidth = windowWidth;
        canvasHeight = windowWidth / gameAspect;
      }
      
      canvas.style.position = 'absolute';
      canvas.style.left = '50%';
      canvas.style.top = '50%';
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;
      canvas.style.transform = 'translate(-50%, -50%)';
    }
  }
  
  private setupUICallbacks(): void {
    this.ui.onSpin(() => this.handleSpin());
    this.ui.onBetUp(() => this.handleBetUp());
    this.ui.onBetDown(() => this.handleBetDown());
    this.ui.onAutoSpin((rounds: number) => this.startAutoSpin(rounds));
    this.ui.onStopAutoSpin(() => this.stopAutoSpin());
    
    // Update UI when game events occur
    this.game.onSpinComplete(() => {
      // Update balance from backend response
      this.ui.updateBalance(this.gameStore.balance, this.gameStore.currency);
      // Update history display
      this.ui.updateHistoryDisplay();
      
      // Continue auto spin if active
      if (this.isAutoSpinning && this.autoSpinRounds > 0) {
        this.autoSpinRounds--;
        this.ui.showAutoSpinRounds(this.autoSpinRounds, this.autoSpinTotal);
        
        if (this.autoSpinRounds > 0) {
          // Delay between auto spins
          setTimeout(() => {
            if (this.isAutoSpinning) {
              this.handleSpin();
            }
          }, 1000);
        } else {
          // Auto spin complete
          this.isAutoSpinning = false;
          this.ui.stopAutoSpin();
        }
      }
    });
  }
  
  private async handleSpin(): Promise<void> {
    if (this.game.isSpinning()) return;
    if (this.gameStore.isLoading) return;
    
    const stake = this.getCurrentStake();
    const totalBet = stake * this.game.getActivePaylines();
    
    if (this.gameStore.balance < totalBet) {
      this.ui.showInsufficientBalance();
      this.stopAutoSpin();
      return;
    }
    
    this.ui.setSpinEnabled(false);
    this.audioManager.play('spin');
    
    try {
      // Place bet through backend
      const bet = await this.gameStore.placeBet(stake);
      
      // Spin with backend reels and win data
      this.game.spinWithResult(
        bet.reels,
        bet.winningLines,
        bet.winningSymbols,
        bet.linePayouts
      );
      
      // Update UI
      this.ui.updateBalance(this.gameStore.balance, this.gameStore.currency);
      
    } catch (error: any) {
      console.error('Spin error:', error);
      this.ui.showError(this.gameStore.error || 'An error occurred');
      this.stopAutoSpin();
    } finally {
      setTimeout(() => {
        this.ui.setSpinEnabled(true);
      }, GAME_CONFIG.SPIN_DURATION + GAME_CONFIG.REELS_COUNT * GAME_CONFIG.REEL_STOP_DELAY + 100);
    }
  }
  
  private startAutoSpin(rounds: number): void {
    this.autoSpinRounds = rounds;
    this.autoSpinTotal = rounds;
    this.isAutoSpinning = true;
    
    this.ui.startAutoSpinUI();
    this.ui.showAutoSpinRounds(this.autoSpinRounds, this.autoSpinTotal);
    
    // Start first spin
    this.handleSpin();
  }
  
  private stopAutoSpin(): void {
    this.isAutoSpinning = false;
    this.autoSpinRounds = 0;
    this.autoSpinTotal = 0;
    // Note: UI's isAutoSpinning flag is managed by the button click handler
  }
  
  private handleBetUp(): void {
    if (this.game.isSpinning() || this.gameStore.isLoading) return;
    
    const levels = this.getBetLevels();
    const oldIndex = this.betIndex;
    this.betIndex = Math.min(this.betIndex + 1, levels.length - 1);
    
    console.log('Bet Up:', {
      oldIndex,
      newIndex: this.betIndex,
      levels: levels,
      oldValue: levels[oldIndex],
      newValue: levels[this.betIndex]
    });
    
    this.updateUI();
  }
  
  private handleBetDown(): void {
    if (this.game.isSpinning() || this.gameStore.isLoading) return;
    
    const levels = this.getBetLevels();
    const oldIndex = this.betIndex;
    this.betIndex = Math.max(this.betIndex - 1, 0);
    
    console.log('Bet Down:', {
      oldIndex,
      newIndex: this.betIndex,
      levels: levels,
      oldValue: levels[oldIndex],
      newValue: levels[this.betIndex]
    });
    
    this.updateUI();
  }
  
  private getBetLevels(): number[] {
    const minVal = this.gameStore.minStake;
    const maxVal = this.gameStore.maxStake;
    
    console.log('Raw minStake:', minVal, 'type:', typeof minVal);
    console.log('Raw maxStake:', maxVal, 'type:', typeof maxVal);
    
    const min = Number(minVal) || 10;
    const max = Number(maxVal) || 1000;
    
    console.log('Bet levels - min:', min, 'max:', max, 'type:', typeof min);
    
    const levels: number[] = [];
    
    // Progressive increments based on stake value
    let current = min;
    
    while (current <= max) {
      levels.push(Number(current));
      
      // Determine next step based on current value
      let step: number;
      if (current < 100) {
        step = 5;
      } else if (current < 500) {
        step = 25;
      } else if (current < 1000) {
        step = 100;
      } else if (current < 5000) {
        step = 500;
      } else {
        step = 1000;
      }
      
      current = current + step;
    }
    
    // Ensure max is included
    if (levels[levels.length - 1] !== max) {
      levels.push(Number(max));
    }
    
    console.log('Generated bet levels:', levels);
    return levels;
  }
  
  private getCurrentStake(): number {
    const levels = this.getBetLevels();
    const stake = levels[this.betIndex] || levels[0] || 10;
    
    console.log('getCurrentStake:', {
      betIndex: this.betIndex,
      levels: levels,
      levelsType: levels.map(l => typeof l),
      selectedStake: stake,
      selectedStakeType: typeof stake
    });
    
    return Number(stake);
  }
  
  private updateUI(): void {
    const stake = this.getCurrentStake();
    this.ui.updateBalance(this.gameStore.balance, this.gameStore.currency);
    this.ui.updateBet(stake);
  }
}

const slotGame = new SlotGame();
slotGame.init().catch(console.error);